// Unlocking Settings: changing settings needs the password (or a two-factor code) entered in the last 15 minutes.
// Signing in counts. When a change is refused because Settings are locked, this window asks for it and the change
// is sent again once unlocked.
import { ApiError, esc, post, setUnlockPrompt } from "./lib.js";
import { state } from "./state.js";
import { PROVIDER_ICON, startUrl, switchedOn } from "./signin.js";
import { PROVIDER_NAME, type SignInProvider } from "../../shared/signin.js";

let open: Promise<boolean> | null = null;

export function askToUnlock(_message = ""): Promise<boolean> {
  if (open) return open;
  open = new Promise<boolean>((resolve) => {
    const twoFactor = !!state.session.twoFactorOn;
    // Google / Microsoft / Apple accounts the person has linked (and that are switched on) can confirm it's them too.
    const linked = (state.session.signInLinked || []).filter((p) => switchedOn().includes(p as SignInProvider)) as SignInProvider[];
    const typed = twoFactor || state.session.hasPassword !== false;
    const back = location.pathname + location.search;
    const sso = linked.length ? `<div class="falcon-unlock-sso">${linked.map((p) =>
      `<a class="secondary falcon-unlock-sso-btn" href="${esc(startUrl(p, "unlock", { back }))}">${PROVIDER_ICON[p]}<span>Confirm with ${esc(PROVIDER_NAME[p])}</span></a>`).join("")}</div>` : "";
    const overlay = document.createElement("div");
    overlay.className = "confirm-overlay falcon-confirm falcon-unlock";
    overlay.innerHTML = `<form class="confirm-box falcon-unlock-box" role="dialog" aria-modal="true" aria-labelledby="unlock-title" novalidate>
  <h3 id="unlock-title">Unlock Settings</h3>
  <p>To change settings, confirm it's you. Settings then stay unlocked for 15 minutes.</p>
  ${typed ? `<div class="form-group"><label for="unlock-secret">${twoFactor ? (state.session.hasPassword === false ? "Two-factor code" : "Two-factor code or password") : "Password"}</label>
    <input id="unlock-secret" name="secret" type="password" autocomplete="current-password" required></div>
  ${twoFactor ? `<small class="muted">${state.session.hasPassword === false ? "Enter the 6-digit code from your authenticator app." : "Enter the 6-digit code from your authenticator app, or your password."}</small>` : ""}` : ""}
  ${sso ? `${typed ? '<div class="falcon-unlock-or">or</div>' : ""}${sso}` : ""}
  <div class="form-message" role="alert" hidden></div>
  <div class="confirm-actions"><button type="button" class="secondary" data-v="no">Cancel</button>${typed ? '<button type="submit" class="primary">Unlock</button>' : ""}</div>
</form>`;
    document.body.appendChild(overlay);
    const form = overlay.querySelector("form")!;
    const input = form.querySelector<HTMLInputElement>("input#unlock-secret") || document.createElement("input");
    const msg = form.querySelector<HTMLElement>(".form-message")!;
    const done = (v: boolean) => {
      overlay.remove();
      document.removeEventListener("keydown", onKey, true);
      open = null;
      resolve(v);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); done(false); } };
    document.addEventListener("keydown", onKey, true);
    overlay.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).closest("[data-v=no]") || e.target === overlay) done(false);
    });
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const v = input.value.trim();
      if (!typed) return;
      if (!v) { input.focus(); return; }
      const btn = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
      btn.disabled = true;
      try {
        const r = await post("/api/account/settings-unlock", twoFactor && /^\d{6}$/.test(v) ? { code: v } : { password: input.value });
        state.session.settingsUnlockedUntil = r.settingsUnlockedUntil;
        document.dispatchEvent(new CustomEvent("falcon:settings-unlocked"));
        done(true);
      } catch (err) {
        msg.hidden = false;
        msg.textContent = (err as Error).message;
        input.select();
        if (err instanceof ApiError && err.code === "LOCKED") setTimeout(() => location.assign("/login"), 2500);
      } finally { btn.disabled = false; }
    });
    input.focus();
  });
  return open;
}

setUnlockPrompt(askToUnlock);
